import { htmlSafe } from '@ember/template';

export type SuggestionType =
  | 'consistency'
  | 'freshness'
  | 'writing-quality'
  | 'missing-info';

export interface SuggestionVariant {
  locale: string;
  current: string;
  // Empty means remove the excerpt; omitted means no proposed fix yet.
  suggested?: string;
}

export interface SuggestionTarget {
  id: string;
  contentId: string;
  contentType: 'page' | 'item';
  folder: string;
  title: string;
  variants: SuggestionVariant[];
}

export interface SuggestionGroup {
  id: string;
  resolutionMode: 'changes' | 'conflict';
  targets: SuggestionTarget[];
}

export interface ContentSuggestion {
  id: string;
  type: SuggestionType;
  title: string;
  reason?: string;
  priority: 'high' | 'medium' | 'low';
  confidence: number;
  groups: SuggestionGroup[];
}

export interface SuggestionRun {
  error?: { code: string; message: string };
  id: string;
  status:
    | 'queued'
    | 'snapshotting'
    | 'analysing'
    | 'validating'
    | 'succeeded'
    | 'failed';
}

export interface SuggestionSet {
  latestRun: SuggestionRun | null;
  runId: string | null;
  snapshotCapturedAt: string | null;
  suggestions: ContentSuggestion[];
}

export const SUGGESTION_ICONS: Record<SuggestionType, string> = {
  consistency: 'arrows-right-left',
  freshness: 'clock',
  'writing-quality': 'book-open',
  'missing-info': 'question-mark-circle',
};

export function previewVariant(
  variants: SuggestionVariant[],
  defaultLocale?: string
) {
  return variants.find(({ locale }) => locale === defaultLocale) ?? variants[0];
}

const PREVIEW_TAGS = new Set(
  'a b blockquote br code del div em h1 h2 h3 h4 h5 h6 hr i ins li ol p pre s span strong sub sup table tbody td tfoot th thead tr u ul'.split(
    ' '
  )
);
const LIST_TYPES = new Map([
  ['1', 'list-decimal'],
  ['a', 'list-[lower-alpha]'],
  ['A', 'list-[upper-alpha]'],
  ['i', 'list-[lower-roman]'],
  ['I', 'list-[upper-roman]'],
]);

export function renderSuggestionHtml(value = '') {
  // Template contents stay inert, including images, while we restrict the preview.
  // The editor sanitizer preserves styles/media; this read-only surface must not.
  const template = document.createElement('template');
  // shortcut: Excerpts are assumed HTML until Kraken exposes the source field format.
  template.innerHTML = value;
  template.content
    .querySelectorAll(
      'script, style, iframe, object, embed, template, noscript, svg, math, img, video, audio, source, input, textarea, select, button, title, link, meta, base'
    )
    .forEach((element) => element.remove());
  template.content.querySelectorAll('*').forEach((element) => {
    if (!PREVIEW_TAGS.has(element.localName)) {
      element.replaceWith(...element.childNodes);
      return;
    }
    if (element.localName === 'a') {
      const destination = element.getAttribute('href')?.trim();
      if (destination && destination !== element.textContent?.trim()) {
        element.after(` (${destination})`);
      }
    }
    // No source CSS, event handlers, IDs or navigable links in review text.
    Array.from(element.attributes).forEach(({ name, value }) => {
      const integer =
        /^-?\d+$/.test(value.trim()) && Number.isSafeInteger(Number(value));
      const listAttribute =
        (element.localName === 'ol' &&
          (name === 'reversed' ||
            (name === 'start' && integer) ||
            (name === 'type' && LIST_TYPES.has(value)))) ||
        (element.localName === 'li' && name === 'value' && integer);
      const cellSpan =
        (element.localName === 'td' || element.localName === 'th') &&
        integer &&
        ((name === 'colspan' && Number(value) > 0) ||
          (name === 'rowspan' && Number(value) >= 0));
      if (!listAttribute && !cellSpan) element.removeAttribute(name);
    });
    if (element.localName === 'ol') {
      // HTML type selectors are case-insensitive; generated classes preserve A/a and I/i.
      element.className =
        LIST_TYPES.get(element.getAttribute('type') ?? '1') ?? 'list-decimal';
    }
  });
  return htmlSafe(template.innerHTML);
}
